import { Dialog, DialogDescription, DialogHeader, ListSelector, ToastLevel, useIntl, useToast } from "@metsooutotec/modes-react-components"
import { FC, useEffect, useState } from "react"
import { getAllocatedSensors, getOnHandSensors } from "../stock-managment/stock-management-helper"
import { SDASensor } from "features/iot/stock-management/models/sensor-dispatch-allocation"
import { DeleteStockAllocation, EditStockAllocation } from "features/iot/stock-management/models/stock-management-models"
import styles from "./dispatch-modal.module.css"
import { convertDateToDDMMMYYYY } from "components/utils/date-formatter-utils"
import stockManagementStore from "../../stores/stock-management-store"
import ModalsButtons from "components/common/modals-buttons"

interface DispatchModalProps {
    partNumber: string
    isOpen: boolean
    onClose: () => void
    sensorToEdit?: SDASensor
    dispatchDate: Date
    customerId: string
    siteId: string | undefined
    siteName: string | undefined
}
interface ListItem {
    title: string
    value: string
}
const DispatchModal: FC<DispatchModalProps> = ({ isOpen, onClose, partNumber, sensorToEdit, dispatchDate, customerId, siteId, siteName }) => {
    const { formatMessage } = useIntl()
    const { setMessage } = useToast()
    const [onHandSensors, setOnHandSensors] = useState<SDASensor[]>([])
    const [listItemsSelector, setListItemsSelector] = useState<ListItem[]>([])
    const [selectedItemId, setSelectedItemId] = useState<string>("")
    const [loading, setLoading] = useState<boolean>(false)
    const [errorMessage, setErrorMessage] = useState<string>("")
    const [isEditing, setIsEditing] = useState<boolean>(Boolean(sensorToEdit?.macAddress))
    const toastMessageTime: number = 4000

    useEffect(() => {
        setIsEditing(Boolean(sensorToEdit?.macAddress))
        const onHandSen = getOnHandSensors(partNumber)
        const allocatedSensors = getAllocatedSensors(partNumber)
        const filteredSensors = allocatedSensors.filter(a => a.customerId === customerId && a.siteId === siteId)
        const listOfSensorsItems = onHandSen
            .filter(s => !filteredSensors.some(a => a.macAddress === s.macAddress))
            .map(sensor => ({
                title: `${sensor.serialNumber} - ${sensor.macAddress}`,
                value: sensor.macAddress,
            }))

        setListItemsSelector(listOfSensorsItems)
        setOnHandSensors(onHandSen)
    }, [isOpen, partNumber])

    const handleSelectedSensorChange = (idValue: string) => {
        setErrorMessage("")
        setSelectedItemId(idValue)
    }

    const handleSubmit = async (e: React.FormEvent): Promise<void> => {
        setLoading(true)
        setErrorMessage("")
        try {
            if (!selectedItemId) {
                setErrorMessage("Nothing has been selected!")
                return
            }

            const sensor = onHandSensors.find(s => s.macAddress === selectedItemId)
            if (!sensor) {
                setMessage("Sensor has not been found!", ToastLevel.ALARM, toastMessageTime)
                return
            }

            // First add the sensor to stock allocation.
            const editStockAllocation: EditStockAllocation = {
                listOfSensorsMacAddress: [sensor.macAddress],
                newCustomerId: customerId,
                newSiteId: siteId || undefined,
                newAllocationDate: convertDateToDDMMMYYYY(dispatchDate),
                partNumber,
            }
            let result = await stockManagementStore.updateStockAllocation(editStockAllocation)

            if (!result.passed) {
                setMessage("Something went wrong!", ToastLevel.ALARM, toastMessageTime)
                return
            }
            if (isEditing && sensorToEdit) {
                //Second remove it.
                const sensorToDelete: DeleteStockAllocation = {
                    partNumber,
                    listOfSensorsMacAddress: [sensorToEdit.macAddress],
                }
                result = await stockManagementStore.deleteStockAllocation(sensorToDelete)
                if (!result.passed) {
                    setErrorMessage(result.message)
                    setMessage("Something went wrong!", ToastLevel.ALARM, toastMessageTime)
                    return
                }
            }

            setMessage("It has been submitted!", ToastLevel.SUCCESS, toastMessageTime)
            onCloseProcess()
        } catch (error) {
            console.error(error) // Log the error or handle it as needed
            setMessage("An error occurred.", ToastLevel.ALARM, toastMessageTime)
        } finally {
            setLoading(false)
        }
    }

    const onCloseProcess = () => {
        onClose()
    }

    const labels = {
        header: formatMessage("Edit a despatched sensor"),
        description: formatMessage("dispatchEdit.description"),
    }

    return (
        <Dialog dimmer open={isOpen} size={"small"}>
            <DialogHeader username={""}>{labels.header}</DialogHeader>
            {errorMessage && <p className={styles.errorMessage}>{errorMessage}</p>}
            <DialogDescription className={styles.dialogDescription}>{labels.description}</DialogDescription>
            {!onHandSensors.length ? (
                <>{formatMessage("sensor.notFound")}</>
            ) : (
                <div className={styles.listSelector}>
                    <ListSelector items={listItemsSelector} onChange={s => handleSelectedSensorChange(s)} selectedItem={selectedItemId} />
                </div>
            )}
            <ModalsButtons handleSubmit={handleSubmit} onCloseProcess={onCloseProcess} isLoading={loading} />
        </Dialog>
    )
}

export default DispatchModal
